import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useCart } from "@/hooks/useCart";

const NAV_ITEMS = [
  { href: "#hero", label: "Início", isRoute: false },
  { href: "/sobre", label: "Sobre", isRoute: true },
  { href: "/cardapio", label: "Cardápio", isRoute: true },
  { href: "#eventos", label: "Eventos", isRoute: false },
  { href: "#contato", label: "Contato", isRoute: false },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";
  const { totalItems, openCart } = useCart();

  useEffect(() => {
    if (!isHome) return;

    const onScroll = () => {
      setScrolled(window.scrollY > 60);

      const sections = NAV_ITEMS.map((item) => item.href.replace("#", ""));
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = document.getElementById(sections[i]);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 140) {
            setActiveSection(sections[i]);
            break;
          }
        }
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHome]);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string, isRoute: boolean) => {
    if (isRoute) return;

    e.preventDefault();

    if (isHome) {
      const target = document.querySelector(href);
      if (target) {
        target.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate("/" + href);
    }

    setOpen(false);
  };

  const transparent = !scrolled && isHome;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        transparent
          ? "bg-transparent"
          : "glass-strong border-b border-background-200/50"
      }`}
    >
      <div className="w-full px-4 sm:px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link
          to="/#hero"
          onClick={() => setOpen(false)}
          className="flex items-center gap-2.5 group cursor-pointer min-w-0"
        >
          <span
            className="w-11 h-11 flex items-center justify-center rounded-xl transition-all duration-300 group-hover:scale-105 shrink-0 overflow-hidden"
          >
            <img
              src="https://public.readdy.ai/ai/img_res/4e0905f2-26ca-4b9d-b973-2bdd15e5e3c5.png"
              alt="Logo Big Mania"
              className="w-full h-full object-contain"
            />
          </span>
          <div className="flex flex-col leading-tight min-w-0">
            <span
              className={`text-base font-heading font-bold tracking-tight truncate ${
                transparent ? "text-background-50" : "text-foreground-950"
              }`}
            >
              Big Mania
            </span>
            <span
              className={`text-[9px] sm:text-[10px] uppercase tracking-[0.18em] truncate ${
                transparent ? "text-background-100/75" : "text-foreground-500"
              }`}
            >
              Sanduíches Naturais
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_ITEMS.map((item) =>
            item.isRoute ? (
              <Link
                key={item.href}
                to={item.href}
                className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap group/nav border-reveal ${
                  location.pathname === item.href
                    ? transparent
                      ? "text-primary-300"
                      : "text-primary-600"
                    : transparent
                      ? "text-background-100/85 hover:text-background-50"
                      : "text-foreground-600 hover:text-foreground-950"
                }`}
              >
                {item.label}
              </Link>
            ) : (
              <a
                key={item.href}
                href={isHome ? item.href : "/" + item.href}
                onClick={(e) => handleClick(e, item.href, item.isRoute)}
                className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap group/nav border-reveal ${
                  activeSection === item.href.replace("#", "") && isHome
                    ? transparent
                      ? "text-primary-300"
                      : "text-primary-600"
                    : transparent
                      ? "text-background-100/85 hover:text-background-50"
                      : "text-foreground-600 hover:text-foreground-950"
                }`}
              >
                {item.label}
              </a>
            )
          )}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="https://wa.me/558534942701"
            target="_blank"
            rel="noopener noreferrer"
            className={`hidden xl:flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              transparent
                ? "text-background-50 hover:text-primary-300"
                : "text-foreground-700 hover:text-primary-600"
            }`}
          >
            <i className="ri-whatsapp-line text-base"></i>
            (85) 3494-2701
          </a>

          <button
            onClick={openCart}
            className={`relative w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              transparent
                ? "text-background-50 hover:bg-background-50/10"
                : "text-foreground-700 hover:bg-background-100"
            }`}
          >
            <i className="ri-shopping-bag-3-line text-xl"></i>
            {totalItems > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center rounded-full bg-accent-500 text-primary-950 text-[10px] font-bold">
                {totalItems > 9 ? "9+" : totalItems}
              </span>
            )}
          </button>

          <a
            href="https://wa.me/558534942701"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-400 transition-all duration-300 hover:-translate-y-0.5 cursor-pointer whitespace-nowrap"
          >
            Pedir pelo WhatsApp
            <i className="ri-arrow-right-up-line text-base transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"></i>
          </a>
        </div>

        <button
          type="button"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className={`lg:hidden w-10 h-10 rounded-lg flex items-center justify-center cursor-pointer transition-colors shrink-0 ${
            transparent
              ? "text-background-50 hover:bg-background-50/10"
              : "text-foreground-900 hover:bg-background-100"
          }`}
        >
          <i className={`ri-${open ? "close" : "menu-3"}-line text-2xl`}></i>
        </button>

        <button
          onClick={openCart}
          className={`lg:hidden relative w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer shrink-0 ${
            transparent
              ? "text-background-50 hover:bg-background-50/10"
              : "text-foreground-700 hover:bg-background-100"
          }`}
        >
          <i className="ri-shopping-bag-3-line text-xl"></i>
          {totalItems > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center rounded-full bg-accent-500 text-primary-950 text-[10px] font-bold">
              {totalItems > 9 ? "9+" : totalItems}
            </span>
          )}
        </button>
      </div>

      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 border-t border-background-200/70 glass-strong ${
          open ? "max-h-[560px]" : "max-h-0"
        }`}
      >
        <div className="px-4 py-4 flex flex-col gap-1">
          {NAV_ITEMS.map((item) =>
            item.isRoute ? (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setOpen(false)}
                className={`px-3 py-3 rounded-lg text-base font-medium cursor-pointer ${
                  location.pathname === item.href
                    ? "bg-primary-50 text-primary-700"
                    : "text-foreground-800 hover:bg-background-100"
                }`}
              >
                {item.label}
              </Link>
            ) : (
              <a
                key={item.href}
                href={isHome ? item.href : "/" + item.href}
                onClick={(e) => handleClick(e, item.href, item.isRoute)}
                className={`px-3 py-3 rounded-lg text-base font-medium cursor-pointer ${
                  activeSection === item.href.replace("#", "") && isHome
                    ? "bg-primary-50 text-primary-700"
                    : "text-foreground-800 hover:bg-background-100"
                }`}
              >
                {item.label}
              </a>
            )
          )}
          <button
            onClick={() => {
              setOpen(false);
              openCart();
            }}
            className="px-3 py-3 rounded-lg text-base font-medium cursor-pointer flex items-center justify-between text-foreground-800 hover:bg-background-100"
          >
            Carrinho
            {totalItems > 0 && (
              <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-500 text-primary-950 text-xs font-bold">
                {totalItems}
              </span>
            )}
          </button>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <a
              href="tel:+558534942701"
              className="flex items-center justify-center gap-2 px-3 py-3 rounded-lg border border-background-300 text-foreground-800 text-sm font-medium cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill"></i> Ligar
            </a>
            <a
              href="https://wa.me/558534942701"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-3 py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer whitespace-nowrap"
            >
              WhatsApp <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}